import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { Skeleton, Widget } from "frontend-components";
import { useTranslation } from "react-i18next";

type StatCardVariant = "pink" | "yellow" | "green";

interface StatCardProps {
    label: string;
    value: number | undefined;
    isPending: boolean;
    isError: boolean;
    icon: IconDefinition;
    variant: StatCardVariant;
    linkHref?: string;
    linkLabel?: string;
}

const VARIANT_CLASSES: Record<StatCardVariant, { tile: string; icon: string }> = {
    pink: {
        tile: "bg-chameleon-pink-10",
        icon: "text-chameleon-pink",
    },
    yellow: {
        tile: "bg-chameleon-yellow-10",
        icon: "text-chameleon-yellow",
    },
    green: {
        tile: "bg-chameleon-green-20",
        icon: "text-compendia-foreground",
    },
};

export function StatCard({
    label,
    value,
    isPending,
    isError,
    icon,
    variant,
    linkHref,
    linkLabel,
}: StatCardProps) {
    const { t } = useTranslation();
    const variantClasses = VARIANT_CLASSES[variant];

    return (
        <Widget>
            <div className="flex h-full flex-col justify-between px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                    <span className="text-sm">{label}</span>
                    <div
                        className={`flex size-8 shrink-0 items-center justify-center rounded-md ${variantClasses.tile}`}
                    >
                        <FontAwesomeIcon
                            icon={icon}
                            className={`size-[18px] ${variantClasses.icon}`}
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-1">
                    {isError ? (
                        <span className="text-micro text-chameleon-pink">
                            {t(
                                "common.somethingWentWrongContactYourSystemAdministratorForMoreInformation",
                            )}
                        </span>
                    ) : isPending || value === undefined ? (
                        <Skeleton className="bg-compendia-greige-60 h-7 w-12 rounded-[3px]" />
                    ) : (
                        <span className="text-compendia-foreground text-[28px] leading-none font-bold tracking-[-0.5px]">
                            {value.toString().padStart(2, "0")}
                        </span>
                    )}
                    {linkHref && linkLabel && (
                        <a
                            href={linkHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-micro text-compendia-gray-60 hover:underline"
                        >
                            {linkLabel} →
                        </a>
                    )}
                </div>
            </div>
        </Widget>
    );
}
